import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Info as InfoIcon } from '@mui/icons-material';
import config from '../../config';
import { authFetch, handleApiResponse } from '../../api/authFetch';
import { describeAdminError } from '../../utils/adminErrors';
import { adminColors, radius, TOUCH_HIT_SX } from '../../theme/adminTheme';

// Phase 4 (admin console v2) — schedule-structure setup for one school,
// reachable from the Schools page (SchoolManagement.js) for a MASTER_ADMIN
// (any school) or a SCHOOL_ADMIN (their own school only — the same access
// GET/PUT /api/admin/schools/{id}/schedule-template itself enforces
// server-side, see _require_schedule_template_access in app/routes/
// admin.py). Everything downstream — a class's Period-based time slots
// (ClassesPage.js), a student's "Today" panel (TodaySchedule.js) — reads
// through whatever's saved here; a school with no template (the default,
// and the common case today) is completely unaffected by any of it.

const KIND_OPTIONS = [
  { value: 'traditional', label: 'Traditional periods', description: 'The same numbered periods, every day.' },
  { value: 'block', label: 'Block schedule', description: 'Fewer, longer periods each day.' },
  { value: 'ab_rotation', label: 'A/B rotation', description: 'Different periods on alternating days.' },
  { value: 'elementary', label: 'Elementary subject blocks', description: 'One block per subject, not a bell schedule.' },
];

function rotationLetterForIndex(index) {
  return String.fromCharCode(65 + index); // 0 -> "A", 1 -> "B", ...
}

function dayPatternOptions(rotationDays) {
  const n = Math.max(0, Number(rotationDays) || 0);
  return Array.from({ length: n }, (_, i) => rotationLetterForIndex(i));
}

// Mirrors app/schedule_service.py::resolve_rotation_day_pattern — same
// modulo math, computed client-side so the preview below reflects the
// draft being edited, not the last-saved version.
function rotationLetterForDate(anchorDateStr, rotationDays, targetDateStr) {
  if (!anchorDateStr || !targetDateStr || !rotationDays || rotationDays <= 0) return null;
  const anchor = new Date(`${anchorDateStr}T00:00:00`);
  const target = new Date(`${targetDateStr}T00:00:00`);
  if (Number.isNaN(anchor.getTime()) || Number.isNaN(target.getTime())) return null;
  const diffDays = Math.round((target - anchor) / 86400000);
  const idx = ((diffDays % rotationDays) + rotationDays) % rotationDays;
  return rotationLetterForIndex(idx);
}

function formatSlotTime(hhmm) {
  if (!hhmm) return '';
  const [hStr, mStr] = String(hhmm).split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = ((h + 11) % 12) + 1;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function KindCard({ option, selected, onSelect, disabled }) {
  return (
    <Paper
      variant="outlined"
      onClick={disabled ? undefined : onSelect}
      role="radio"
      aria-checked={selected}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onSelect();
        }
      }}
      sx={{
        p: 1.75,
        cursor: disabled ? 'default' : 'pointer',
        borderRadius: radius.control,
        borderColor: selected ? adminColors.accent : 'rgba(24,24,27,0.12)',
        borderWidth: selected ? 2 : 1,
        backgroundColor: selected ? adminColors.accentLight : adminColors.surfaceRaised,
        opacity: disabled ? 0.6 : 1,
        transition: 'border-color 0.12s ease, background-color 0.12s ease',
        '&:hover': disabled ? undefined : { borderColor: adminColors.accent },
        '&:focus-visible': { outline: `2px solid ${adminColors.accent}`, outlineOffset: 2 },
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700, color: adminColors.ink }}>
        {option.label}
      </Typography>
      <Typography variant="caption" sx={{ color: adminColors.inkSoft, display: 'block', mt: 0.25 }}>
        {option.description}
      </Typography>
    </Paper>
  );
}

let clientIdCounter = 0;
function nextClientId() {
  clientIdCounter += 1;
  return `new-${clientIdCounter}`;
}

export default function ScheduleTemplateDialog({ open, onClose, school, onSaved }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [formError, setFormError] = useState('');

  const [kind, setKind] = useState('traditional');
  const [templateName, setTemplateName] = useState('');
  const [rotationDays, setRotationDays] = useState(2);
  const [rotationAnchorDate, setRotationAnchorDate] = useState('');
  const [periods, setPeriods] = useState([]); // [{clientId, id?, label, day_pattern, start_time, end_time}]
  const [previewDate, setPreviewDate] = useState(todayIso());

  const saveErrorRef = useRef(null);

  useEffect(() => {
    if (!open || !school) return undefined;
    setLoading(true);
    setLoadError('');
    setSaveError('');
    setFormError('');
    const controller = new AbortController();
    authFetch(`${config.API_BASE_URL}/api/admin/schools/${school.id}/schedule-template`, {}, { signal: controller.signal })
      .then((res) => handleApiResponse(res, navigate))
      .then((data) => {
        if (data === null || controller.signal.aborted) return; // 401 — already redirected
        if (data.template) {
          setKind(data.template.kind || 'traditional');
          setTemplateName(data.template.name || '');
          setRotationDays(data.template.rotation_days || 2);
          setRotationAnchorDate(data.template.rotation_anchor_date || '');
        } else {
          setKind('traditional');
          setTemplateName('');
          setRotationDays(2);
          setRotationAnchorDate('');
        }
        setPeriods(
          (data.periods || []).map((p) => ({
            clientId: `existing-${p.id}`,
            id: p.id,
            label: p.label,
            day_pattern: p.day_pattern,
            start_time: p.start_time,
            end_time: p.end_time,
          }))
        );
        setPreviewDate(data.template?.rotation_anchor_date || todayIso());
        setLoading(false);
      })
      .catch((err) => {
        if (err?.name === 'AbortError' || controller.signal.aborted) return;
        setLoadError(describeAdminError(err, "Couldn't load this school's schedule."));
        setLoading(false);
      });
    return () => controller.abort();
  }, [open, school, navigate]);

  useEffect(() => {
    if (saveError) saveErrorRef.current?.focus();
  }, [saveError]);

  const isRotation = kind === 'ab_rotation';
  const patternOptions = useMemo(() => dayPatternOptions(rotationDays), [rotationDays]);

  const addPeriod = () => {
    setPeriods((prev) => [...prev, { clientId: nextClientId(), label: '', day_pattern: null, start_time: '', end_time: '' }]);
  };

  const updatePeriod = (clientId, patch) => {
    setPeriods((prev) => prev.map((p) => (p.clientId === clientId ? { ...p, ...patch } : p)));
  };

  const removePeriod = (clientId) => {
    setPeriods((prev) => prev.filter((p) => p.clientId !== clientId));
  };

  // Client-side mirror of _validate_schedule_template_payload in
  // app/routes/admin.py — every rule here also gets re-checked server-side,
  // so this is purely to give a specific, inline error before a round trip
  // rather than a source of truth.
  const validationError = useMemo(() => {
    if (isRotation) {
      if (!rotationDays || rotationDays < 1) return 'Rotation days must be at least 1.';
      if (!rotationAnchorDate) return 'Pick an anchor date for the A/B rotation.';
    }
    if (periods.length === 0) return 'Add at least one period.';
    for (const p of periods) {
      if (!p.label || !p.label.trim()) return 'Every period needs a label.';
      if (!p.start_time || !p.end_time) return `"${p.label}" needs both a start and end time.`;
      if (p.end_time <= p.start_time) return `"${p.label}"'s end time must be after its start time.`;
    }
    return '';
  }, [isRotation, rotationDays, rotationAnchorDate, periods]);

  const previewRotationLetter = isRotation ? rotationLetterForDate(rotationAnchorDate, rotationDays, previewDate) : null;
  const previewPeriods = useMemo(() => {
    const applicable = isRotation
      ? periods.filter((p) => !p.day_pattern || p.day_pattern === previewRotationLetter)
      : periods;
    return [...applicable].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
  }, [periods, isRotation, previewRotationLetter]);

  const handleSave = async () => {
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setFormError('');
    setSaveError('');
    setSaving(true);
    try {
      const payload = {
        kind,
        rotation_days: isRotation ? Number(rotationDays) || 1 : 1,
        rotation_anchor_date: isRotation ? rotationAnchorDate : null,
        name: templateName.trim() || null,
        periods: periods.map((p, idx) => ({
          label: p.label.trim(),
          ordinal: idx + 1,
          day_pattern: isRotation ? p.day_pattern || null : null,
          start_time: p.start_time,
          end_time: p.end_time,
        })),
      };
      const res = await authFetch(`${config.API_BASE_URL}/api/admin/schools/${school.id}/schedule-template`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      onSaved(data);
    } catch (err) {
      setSaveError(describeAdminError(err, "This schedule couldn't be saved."));
    } finally {
      setSaving(false);
    }
  };

  if (!school) return null;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth aria-labelledby="schedule-template-title">
      <DialogTitle id="schedule-template-title" sx={{ fontWeight: 700 }}>
        {school.name}&rsquo;s schedule
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" variant="outlined" icon={<InfoIcon fontSize="small" />} sx={{ mb: 2.5 }}>
          Classes can reference these periods for their time slots, and every student&rsquo;s daily schedule
          resolves through this template. Changing it doesn&rsquo;t move any existing class times — a class that
          already uses a period keeps following that period&rsquo;s own times, wherever they end up.
        </Alert>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={26} />
          </Box>
        ) : loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: adminColors.ink, mb: 1 }}>
              1. Structure
            </Typography>
            <Box role="radiogroup" aria-label="Schedule structure" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25, mb: 2.5 }}>
              {KIND_OPTIONS.map((opt) => (
                <KindCard key={opt.value} option={opt} selected={kind === opt.value} onSelect={() => setKind(opt.value)} disabled={saving} />
              ))}
            </Box>

            <TextField
              label="Name (optional)"
              placeholder="e.g. Fall 2026 Bell Schedule"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              fullWidth
              disabled={saving}
              sx={{ mb: 2.5 }}
            />

            {isRotation && (
              <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
                <TextField
                  label="Rotation days"
                  type="number"
                  inputProps={{ min: 1, max: 10 }}
                  value={rotationDays}
                  onChange={(e) => setRotationDays(Math.max(1, Number(e.target.value) || 1))}
                  disabled={saving}
                  helperText={`Day letters: ${patternOptions.join(', ')}`}
                  sx={{ minWidth: 160 }}
                />
                <TextField
                  label="Anchor date"
                  type="date"
                  value={rotationAnchorDate}
                  onChange={(e) => setRotationAnchorDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={saving}
                  helperText="The date Day A falls on"
                  sx={{ minWidth: 180 }}
                />
              </Box>
            )}

            <Divider sx={{ mb: 2.5, borderColor: 'rgba(24,24,27,0.08)' }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: adminColors.ink, mb: 1 }}>
              2. Periods
            </Typography>
            {periods.length === 0 && (
              <Typography variant="body2" sx={{ color: adminColors.inkSoft, mb: 1.5 }}>
                No periods yet — add at least one below.
              </Typography>
            )}
            <Box sx={{ mb: 1.5 }}>
              {periods.map((p) => (
                <Box key={p.clientId} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    label="Label"
                    placeholder="e.g. Period 1"
                    value={p.label}
                    onChange={(e) => updatePeriod(p.clientId, { label: e.target.value })}
                    disabled={saving}
                    sx={{ minWidth: 160, flex: '1 1 160px' }}
                  />
                  {isRotation && (
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel id={`period-pattern-${p.clientId}`} shrink>Day</InputLabel>
                      <Select
                        labelId={`period-pattern-${p.clientId}`}
                        label="Day"
                        displayEmpty
                        value={p.day_pattern || ''}
                        onChange={(e) => updatePeriod(p.clientId, { day_pattern: e.target.value || null })}
                        disabled={saving}
                        sx={{ '& .MuiSelect-select': TOUCH_HIT_SX }}
                      >
                        <MenuItem value=""><em>Every day</em></MenuItem>
                        {patternOptions.map((letter) => (
                          <MenuItem key={letter} value={letter}>Day {letter}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <TextField
                    type="time"
                    size="small"
                    label="Start"
                    InputLabelProps={{ shrink: true }}
                    value={p.start_time}
                    onChange={(e) => updatePeriod(p.clientId, { start_time: e.target.value })}
                    disabled={saving}
                    sx={{ minWidth: 120 }}
                  />
                  <TextField
                    type="time"
                    size="small"
                    label="End"
                    InputLabelProps={{ shrink: true }}
                    value={p.end_time}
                    onChange={(e) => updatePeriod(p.clientId, { end_time: e.target.value })}
                    disabled={saving}
                    sx={{ minWidth: 120 }}
                  />
                  <Tooltip title="Remove period">
                    <IconButton
                      size="small"
                      onClick={() => removePeriod(p.clientId)}
                      disabled={saving}
                      aria-label={`Remove ${p.label || 'this period'}`}
                      sx={{ ...TOUCH_HIT_SX, color: adminColors.inkSoft, '&:hover': { color: '#dc2626' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
            <Button
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={addPeriod}
              disabled={saving}
              sx={{ ...TOUCH_HIT_SX, fontWeight: 600, color: adminColors.accent, mb: 2.5 }}
            >
              Add a period
            </Button>

            {formError && (
              <Alert severity="warning" variant="outlined" sx={{ mb: 2.5 }}>
                {formError}
              </Alert>
            )}

            <Divider sx={{ mb: 2.5, borderColor: 'rgba(24,24,27,0.08)' }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: adminColors.ink, mb: 1 }}>
              Preview a day
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
              {isRotation && (
                <TextField
                  size="small"
                  type="date"
                  label="Date"
                  value={previewDate}
                  onChange={(e) => setPreviewDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 170 }}
                />
              )}
              {isRotation && previewRotationLetter && (
                <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
                  Resolves to <strong>Day {previewRotationLetter}</strong>
                </Typography>
              )}
              {isRotation && !rotationAnchorDate && (
                <Typography variant="caption" sx={{ color: adminColors.inkSoft }}>
                  Pick an anchor date above to preview a rotation day.
                </Typography>
              )}
            </Box>
            {previewPeriods.length === 0 ? (
              <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
                Nothing scheduled for this day yet.
              </Typography>
            ) : (
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: radius.control, borderColor: 'rgba(24,24,27,0.08)', bgcolor: adminColors.surface }}>
                {previewPeriods.map((p) => (
                  <Box key={p.clientId} sx={{ display: 'flex', gap: 1.5, py: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: adminColors.ink, minWidth: 130 }}>
                      {formatSlotTime(p.start_time)}–{formatSlotTime(p.end_time)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>{p.label || 'Untitled period'}</Typography>
                  </Box>
                ))}
              </Paper>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, flexDirection: 'column', alignItems: 'stretch', gap: saveError ? 1.5 : 0 }}>
        {saveError && (
          <Alert ref={saveErrorRef} severity="error" role="alert" aria-live="assertive" tabIndex={-1} sx={{ width: '100%' }}>
            {saveError}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || loading || Boolean(loadError)}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ ...TOUCH_HIT_SX, backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark } }}
          >
            {saving ? 'Saving…' : 'Save schedule'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
